<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { fetchWooCommerceAbandonedCheckouts } from '../../api/woocommerce';
import WooCommerceBrandIcon from '../../components/WooCommerceBrandIcon.vue';
import WooCommerceStoreSwitcher from '../../components/WooCommerceStoreSwitcher.vue';
import {
  buildWooCommerceStoreOptions,
  isWooCommerceHookSelectionValid,
} from '../../helpers/stores';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const isLoading = ref(true);
const isRefreshing = ref(false);
const records = ref([]);
const error = ref('');
const openActionMenuId = ref(null);
const pagination = ref({
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
  itemsPerPage: 25,
});
const accountId = computed(() => Number(route.params.accountId));
const selectedHookId = computed(() => route.params.hookId?.toString() || '');
const currentPage = computed(() => {
  const page = Number(route.query.page || 1);
  return Number.isFinite(page) && page > 0 ? page : 1;
});
const integration = computed(() => {
  const getter = store.getters['integrations/getIntegration'];
  return getter ? getter('woocommerce', {}) : { hooks: [] };
});
const availableStores = computed(() =>
  buildWooCommerceStoreOptions(integration.value?.hooks || [])
);
const selectedStoreLabel = computed(
  () =>
    route.query.store_name?.toString() ||
    availableStores.value.find(
      storeOption => storeOption.id === selectedHookId.value
    )?.label ||
    ''
);
const selectedHooks = computed(() => {
  const hooks = integration.value?.hooks || [];

  return selectedHookId.value
    ? hooks.filter(hook => String(hook.id) === selectedHookId.value)
    : hooks;
});
const translatedError = computed(() =>
  error.value ? t('WOOCOMMERCE_PAGES.ABANDONED.ERROR') : ''
);
const showPaginationFooter = computed(() => pagination.value.totalPages > 1);
const pendingBackfillMessage = computed(() => {
  const hasPendingState = selectedHooks.value.some(hook => {
    const status =
      hook.settings?.abandoned_carts_backfill?.status ||
      hook.abandoned_carts_backfill?.status;
    const cachedCount =
      hook.settings?.cached_abandoned_checkouts_count ||
      hook.cached_abandoned_checkouts_count ||
      0;

    return ['queued', 'running'].includes(status) && cachedCount === 0;
  });

  return hasPendingState ? t('WOOCOMMERCE_PAGES.ABANDONED.EMPTY_PENDING') : '';
});

const customerDetails = checkout =>
  [checkout.customer_email || '—', checkout.customer_phone || '—'].join(' · ');

const checkoutDetails = checkout =>
  [checkout.total || '—', checkout.currency || ''].filter(Boolean).join(' ');

const checkoutActionMenuId = checkout =>
  checkout.id || checkout.token || checkout.checkout_url || checkout.customer_email;

const toggleActionMenu = checkout => {
  const menuId = checkoutActionMenuId(checkout);
  openActionMenuId.value = openActionMenuId.value === menuId ? null : menuId;
};

const closeActionMenu = () => {
  openActionMenuId.value = null;
};

const runCheckoutAction = action => {
  closeActionMenu();
  action();
};

const loadRecords = async ({ refresh = false } = {}) => {
  try {
    closeActionMenu();
    if (
      !isWooCommerceHookSelectionValid(
        integration.value?.hooks || [],
        selectedHookId.value
      )
    ) {
      await router.replace({
        name: route.name,
        params: {
          ...route.params,
          hookId: undefined,
        },
        query: {
          ...route.query,
          page: undefined,
          store_name: undefined,
        },
      });
      return;
    }

    if (refresh) {
      isRefreshing.value = true;
    } else {
      isLoading.value = true;
    }
    error.value = '';
    const { data } = await fetchWooCommerceAbandonedCheckouts(accountId.value, {
      page: currentPage.value,
      per_page: pagination.value.itemsPerPage,
      hook_id: selectedHookId.value || undefined,
      refresh: refresh || undefined,
    });
    records.value = data.abandoned_checkouts || [];
    pagination.value = {
      totalCount: data.meta?.total_count || records.value.length,
      currentPage: data.meta?.current_page || currentPage.value,
      totalPages: data.meta?.total_pages || 0,
      itemsPerPage: data.meta?.items_per_page || pagination.value.itemsPerPage,
    };
  } catch (err) {
    if (err?.response?.status === 404 && selectedHookId.value) {
      await store.dispatch('integrations/get', 'woocommerce').catch(() => {});
      await router.replace({
        name: route.name,
        params: {
          ...route.params,
          hookId: undefined,
        },
        query: {
          ...route.query,
          page: undefined,
          store_name: undefined,
        },
      });
      return;
    }

    error.value = 'WOOCOMMERCE_PAGES.ABANDONED.ERROR';
  } finally {
    isLoading.value = false;
    isRefreshing.value = false;
  }
};

const updatePage = page => {
  router.push({
    name: route.name,
    params: route.params,
    query: {
      ...route.query,
      page: page > 1 ? String(page) : undefined,
    },
  });
};

const openContact = contactId => {
  if (!contactId) return;
  router.push({
    name: 'contacts_edit',
    params: { accountId: accountId.value, contactId },
  });
};

const openCheckout = checkoutUrl => {
  if (!checkoutUrl) return;

  window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
};

const refreshRecords = () => {
  loadRecords({ refresh: true });
};

onMounted(async () => {
  await store.dispatch('integrations/get', 'woocommerce').catch(() => {});
  loadRecords();
});

watch(selectedHookId, () => {
  if (currentPage.value !== 1) {
    updatePage(1);
    return;
  }

  loadRecords();
});

watch(
  () => route.query.page,
  () => {
    loadRecords();
  }
);
</script>

<template>
  <SettingsLayout :is-loading="isLoading" class="gap-6">
    <template #header>
      <div
        data-target="woocommerce-abandoned-checkouts-page"
        class="w-full max-w-[72rem] mx-auto px-4 sm:px-6 pt-4 sm:pt-6"
      >
        <div class="flex flex-col gap-4 rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
          <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div class="flex min-w-0 gap-3">
              <WooCommerceBrandIcon :size="28" class="shrink-0" />
              <div class="min-w-0">
                <h1 class="mb-1 text-2xl sm:text-3xl font-semibold text-n-slate-12">
                  {{ $t('WOOCOMMERCE_PAGES.ABANDONED.TITLE') }}
                </h1>
                <p class="mb-0 max-w-4xl text-sm sm:text-base text-n-slate-11">
                  {{
                    selectedStoreLabel
                      ? $t('WOOCOMMERCE_PAGES.ABANDONED.DESCRIPTION_FOR_STORE', {
                          store: selectedStoreLabel,
                        })
                      : $t('WOOCOMMERCE_PAGES.ABANDONED.DESCRIPTION')
                  }}
                </p>
              </div>
            </div>
            <Button
              icon="i-lucide-refresh-cw"
              variant="outline"
              color="slate"
              class="w-full justify-center md:w-auto"
              :disabled="isRefreshing"
              :label="$t('WOOCOMMERCE_PAGES.ABANDONED.REFRESH')"
              @click="refreshRecords"
            />
          </div>

          <div
            v-if="availableStores.length"
            data-target="woocommerce-abandoned-store-switcher"
            class="flex items-center justify-start border-t border-n-weak pt-4"
          >
            <WooCommerceStoreSwitcher
              :stores="availableStores"
              :selected-hook-id="selectedHookId"
              show-all-stores
            />
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div class="w-full max-w-[72rem] mx-auto px-4 sm:px-6 pb-6 flex flex-col gap-4 sm:gap-6">
        <div
          v-if="error"
          class="rounded-md border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-12"
        >
          {{ translatedError }}
        </div>
        <div
          v-else-if="!records.length"
          class="rounded-md border border-dashed border-n-weak bg-n-alpha-2 p-5 sm:p-6 text-sm text-n-slate-11"
        >
          <p class="mb-3 text-base font-medium text-n-slate-12">
            {{ pendingBackfillMessage || $t('WOOCOMMERCE_PAGES.ABANDONED.EMPTY') }}
          </p>
          <p class="mb-4 text-sm text-n-slate-11">
            {{ $t('WOOCOMMERCE_PAGES.ABANDONED.EMPTY_HELP') }}
          </p>
          <Button
            icon="i-lucide-refresh-cw"
            variant="outline"
            color="slate"
            class="w-full justify-center sm:w-auto"
            :disabled="isRefreshing"
            :label="$t('WOOCOMMERCE_PAGES.ABANDONED.REFRESH')"
            @click="refreshRecords"
          />
        </div>
        <div v-else class="grid gap-4">
          <div
            v-for="checkout in records"
            :key="checkout.id || checkout.token"
            class="rounded-md border border-n-weak bg-n-alpha-2 p-4 shadow-sm"
          >
            <div
              class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
            >
              <div class="space-y-1 min-w-0">
                <p class="mb-0 text-lg font-semibold text-n-slate-12">
                  {{
                    $t('WOOCOMMERCE_PAGES.ABANDONED.CHECKOUT_ID', {
                      id: checkout.id || checkout.token || '—',
                    })
                  }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{
                    checkout.customer_name ||
                    checkout.customer_email ||
                    checkout.customer_phone ||
                    '—'
                  }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ customerDetails(checkout) }}
                </p>
                <div class="flex flex-wrap gap-2 pt-2">
                  <span class="rounded-md bg-n-alpha-3 px-2 py-1 text-xs font-medium text-n-slate-11">
                    {{ checkoutDetails(checkout) }}
                  </span>
                  <span
                    v-if="checkout.source_label"
                    class="rounded-md bg-n-alpha-3 px-2 py-1 text-xs font-medium text-n-slate-11"
                  >
                    {{ checkout.source_label }}
                  </span>
                </div>
              </div>
              <div class="relative flex w-full items-center gap-2 sm:w-auto lg:justify-end">
                <Button
                  v-if="checkout.checkout_url"
                  icon="i-lucide-shopping-cart"
                  size="sm"
                  class="min-w-0 flex-1 justify-center text-sm sm:flex-none"
                  :label="$t('WOOCOMMERCE_PAGES.OPEN_CHECKOUT')"
                  @click="openCheckout(checkout.checkout_url)"
                />
                <Button
                  v-else-if="checkout.contact_id"
                  icon="i-lucide-user-round"
                  size="sm"
                  class="min-w-0 flex-1 justify-center text-sm sm:flex-none"
                  :label="$t('WOOCOMMERCE_PAGES.OPEN_CONTACT_TO_MESSAGE')"
                  @click="openContact(checkout.contact_id)"
                />
                <OnClickOutside @trigger="closeActionMenu">
                  <div class="relative">
                    <button
                      type="button"
                      class="flex size-9 items-center justify-center rounded-md border border-n-weak bg-n-alpha-2 text-n-slate-11 transition hover:bg-n-alpha-3 hover:text-n-slate-12"
                      :aria-label="$t('WOOCOMMERCE_PAGES.MORE_ACTIONS')"
                      :title="$t('WOOCOMMERCE_PAGES.MORE_ACTIONS')"
                      @click="toggleActionMenu(checkout)"
                    >
                      <span class="i-lucide-ellipsis-vertical size-4" />
                    </button>
                    <div
                      v-if="openActionMenuId === checkoutActionMenuId(checkout)"
                      class="absolute right-0 top-11 z-20 w-64 overflow-hidden rounded-md border border-n-weak bg-n-solid-2 p-1 shadow-lg"
                    >
                      <button
                        type="button"
                        class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="!checkout.contact_id"
                        @click="
                          runCheckoutAction(() => openContact(checkout.contact_id))
                        "
                      >
                        <span class="i-lucide-user-round size-4 text-n-slate-10" />
                        <span>{{ $t('WOOCOMMERCE_PAGES.OPEN_CONTACT_TO_MESSAGE') }}</span>
                      </button>
                      <button
                        v-if="checkout.checkout_url"
                        type="button"
                        class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                        @click="
                          runCheckoutAction(() =>
                            openCheckout(checkout.checkout_url)
                          )
                        "
                      >
                        <span class="i-lucide-external-link size-4 text-n-slate-10" />
                        <span>{{ $t('WOOCOMMERCE_PAGES.OPEN_CHECKOUT') }}</span>
                      </button>
                    </div>
                  </div>
                </OnClickOutside>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <PaginationFooter
      v-if="showPaginationFooter && !error"
      :current-page="pagination.currentPage"
      :total-items="pagination.totalCount"
      :items-per-page="pagination.itemsPerPage"
      @update:current-page="updatePage"
    />
  </SettingsLayout>
</template>
